import { fetchAllRows } from '@kit/shared/lib/fetch-all-rows';
import { captureServiceError } from '@kit/shared/lib/sentry-helpers';
import { Result, ServiceError } from '@kit/shared/result';

import { mirrorCurrentLocationToProfile } from './dancer-location-upsert';
import type { DancerLocationContext } from './dancer-location.context';
import type {
  CreateLocationInput,
  DancerLocation,
} from './dancer-location.types';

/**
 * Add a new location
 */
export async function addLocation(
  ctx: DancerLocationContext,
  userId: string,
  input: CreateLocationInput,
): Promise<Result<DancerLocation>> {
  const logContext = {
    method: 'addLocation',
    userId,
    city: input.city,
    startDate: input.start_date,
    endDate: input.end_date,
  };

  ctx.log('info', 'Adding new location', logContext);

  try {
    // Validate date format
    const startDate = new Date(input.start_date);
    if (isNaN(startDate.getTime())) {
      return Result.fail(
        ServiceError.validation('Invalid start_date format. Use YYYY-MM-DD'),
      );
    }

    if (input.end_date) {
      const endDate = new Date(input.end_date);
      if (isNaN(endDate.getTime())) {
        return Result.fail(
          ServiceError.validation('Invalid end_date format. Use YYYY-MM-DD'),
        );
      }

      // Validate end_date is after start_date
      if (endDate <= startDate) {
        return Result.fail(
          ServiceError.validation('end_date must be after start_date'),
        );
      }
    }

    // Check for overlapping date ranges
    const overlapResult = await checkForOverlap(
      ctx,
      userId,
      input.start_date,
      input.end_date || null,
      null,
    );

    if (!overlapResult.isSuccess) {
      return Result.fail(overlapResult.getError());
    }

    if (overlapResult.getValue()) {
      return Result.fail(
        ServiceError.validation(
          'Location dates overlap with an existing location',
        ),
      );
    }

    // If set_as_current is true, first unset all other current locations
    if (input.set_as_current) {
      const { error: unsetError } = await ctx.client
        .from('artist_locations')
        .update({ is_current: false })
        .eq('user_id', userId)
        .eq('is_current', true);

      if (unsetError) {
        ctx.log('error', 'Failed to unset current locations', {
          ...logContext,
          error: unsetError.message,
        });
        return Result.fail(
          ServiceError.database(unsetError.message, unsetError),
        );
      }
    }

    const insertPayload = {
      user_id: userId,
      city: input.city,
      country: input.country,
      country_code: input.country_code || null,
      place_id: input.place_id || null,
      start_date: input.start_date,
      end_date: input.end_date || null,
      is_current: input.set_as_current ?? false,
      display_order: 0,
      latitude: input.latitude ?? null,
      longitude: input.longitude ?? null,
    };

    const { data, error } = await ctx.client
      .from('artist_locations')
      .insert(insertPayload)
      .select()
      .single();

    if (error) {
      ctx.log('error', 'Failed to add location', {
        ...logContext,
        error: error.message,
        errorCode: error.code,
      });
      return Result.fail(ServiceError.database(error.message, error));
    }

    ctx.log('info', 'Location added successfully', {
      ...logContext,
      locationId: data.id,
    });

    // Mirror current location → `profiles.{city, country_of_residence}`.
    // The audition application registry binds form keys `city` /
    // `residenceCountry` to those columns, so without this write
    // freshly-onboarded dancers keep them NULL and any audition that
    // requires those keys blocks. The `artist_locations` table holds
    // date-ranged history; `profiles` holds the canonical "current"
    // single value used everywhere else.
    if (input.set_as_current) {
      await mirrorCurrentLocationToProfile(ctx, userId, {
        city: input.city,
        country_code: input.country_code ?? null,
      });
    }

    return Result.ok(data);
  } catch (error) {
    const serviceError = ServiceError.internal(
      error instanceof Error ? error.message : 'Unknown error',
      error,
    );
    captureServiceError(serviceError, {
      action: 'addLocation',
      step: 'execution',
    });
    return Result.fail(serviceError);
  }
}

/**
 * Check if dates overlap with existing locations (excluding specified locationId)
 * Returns true if overlap exists
 */
export async function checkForOverlap(
  ctx: DancerLocationContext,
  userId: string,
  startDate: string,
  endDate: string | null,
  excludeLocationId: string | null,
): Promise<Result<boolean>> {
  try {
    let query = ctx.client
      .from('artist_locations')
      .select('id, start_date, end_date, is_current')
      .eq('user_id', userId);

    if (excludeLocationId) {
      query = query.neq('id', excludeLocationId);
    }

    const orderedQuery = query.order('id');
    const { data, error } = await fetchAllRows((from, to) =>
      orderedQuery.range(from, to),
    );

    if (error) {
      return Result.fail(ServiceError.database(error.message, error));
    }

    if (!data || data.length === 0) {
      return Result.ok(false);
    }

    // Check for overlaps
    const newStart = new Date(startDate);
    const newEnd = endDate ? new Date(endDate) : null;

    for (const location of data) {
      // Skip the singular canonical current-residence marker. It is an
      // open-ended "you live here" row upserted by the residence surfaces
      // (BAL-635), not a date-ranged availability entry — so it must not
      // block adding date-ranged tour / future locations alongside it, and
      // it is REPLACED (via upsertCurrentLocation) rather than overlapped.
      if (location.is_current) continue;

      const existingStart = new Date(location.start_date);
      const existingEnd = location.end_date
        ? new Date(location.end_date)
        : null;

      // Case 1: New period is open-ended
      if (!newEnd) {
        // Overlaps if existing location starts after or during new start
        if (existingStart >= newStart) {
          return Result.ok(true);
        }
        // Overlaps if existing location is open-ended and starts before new start
        if (!existingEnd && existingStart < newStart) {
          return Result.ok(true);
        }
        // Overlaps if existing location ends after new start
        if (existingEnd && existingEnd >= newStart) {
          return Result.ok(true);
        }
      }
      // Case 2: New period has end date
      else {
        // Overlaps if existing location is open-ended and starts before new end
        if (!existingEnd && existingStart < newEnd) {
          return Result.ok(true);
        }
        // Overlaps if date ranges intersect
        if (existingEnd) {
          const overlap =
            (newStart >= existingStart && newStart < existingEnd) ||
            (newEnd > existingStart && newEnd <= existingEnd) ||
            (newStart <= existingStart && newEnd >= existingEnd);

          if (overlap) {
            return Result.ok(true);
          }
        }
      }
    }

    return Result.ok(false);
  } catch (error) {
    return Result.fail(
      ServiceError.internal(
        error instanceof Error ? error.message : 'Unknown error',
        error,
      ),
    );
  }
}
